// What a downloaded video is CALLED, client side.
//
// The object in storage is `compiled-1724…​.mp4` (a photo compile) or
// `combined-1788…​.mp4` (a footage combine) — names that exist so repeated runs
// can't collide, and which are meaningless in a user's Downloads folder. This
// module turns a project into the name the user actually gets:
// `{project} – {capture range}.mp4`.
//
// TWIN of `_capture_range` / `compiled_video_filename` in
// packages/containers/generatevideo/processing/photo_compile.py, which stamps
// the SAME string onto the object's Content-Disposition at upload time — and,
// since the combined-video filename work, of the identical stamp
// video_concat.py applies to a combine's output. The two must agree literally —
// when they disagree the browser gets one name from the header and this module
// computes another, which is precisely the condition CompiledVideoCard uses to
// decide between a streaming download and a blob fallback. All three test
// suites pin the format with identical literal expectations
// (e.g. `My Deck – Jul 9–16, 2026.mp4`); change one and you must change them.

import { normalizeSelection, resolveSelection, UNKNOWN_DAY } from './daySelection';
import { ORIGIN_VIDEO_CONCAT, resolveVideoOrigin } from './videoOrigin';

// Fallback when a project has no usable name. Not "Untitled": the file is a
// timelapse, and this is what the user will be scrolling past in Finder.
export const DEFAULT_VIDEO_NAME = 'Timelapse';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// U+2013. Named rather than inlined because the grammar uses it two ways —
// unspaced INSIDE a range of days in one month (`Jul 9–16`), spaced BETWEEN
// two dates that each carry a month (`Jul 28 – Aug 4`) — and a stray hyphen or
// em dash in either position silently breaks the header/filename match.
const EN_DASH = '–';

// Characters no mainstream filesystem accepts, plus C0/C7F control codes.
// Replaced (not stripped) so `Deck: North` reads as `Deck- North` rather than
// losing the separator that made the name legible.
// eslint-disable-next-line no-control-regex
const ILLEGAL_RE = /[/\\:*?"<>|\x00-\x1f\x7f]/g;

/** Parse an ISO `YYYY-MM-DD` day key, or null if it isn't one. */
function parseDay(day) {
  const m = DAY_RE.exec(typeof day === 'string' ? day.trim() : '');
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const date = Number(m[3]);
  if (month < 1 || month > 12 || date < 1 || date > 31) return null;
  return { year, month, date };
}

/**
 * Human capture range for a pair of ISO day keys.
 *
 * Four shapes, in increasing order of how much context the second date needs:
 *
 *   one day                 `Jul 9, 2026`
 *   same month              `Jul 9–16, 2026`          (unspaced en dash)
 *   same year, two months   `Jul 28 – Aug 4, 2026`    (spaced en dash)
 *   two years               `Dec 28, 2025 – Jan 4, 2026`
 *
 * Returns '' when neither end parses — callers drop the range entirely rather
 * than emit a file called `My Deck – .mp4`. A single parsable end is treated as
 * a single day, which is what a half-written `compiledFrom` amounts to.
 */
export function formatCaptureRange(firstDay, lastDay) {
  let a = parseDay(firstDay);
  let b = parseDay(lastDay);
  if (!a && !b) return '';
  if (!a) a = b;
  if (!b) b = a;
  // Tolerate a swapped pair rather than emitting a backwards range: the days
  // arrive from two different sources (the worker's snapshot, or a resolved
  // selection) and only one of them is guaranteed sorted.
  if (
    a.year > b.year
    || (a.year === b.year && (a.month > b.month || (a.month === b.month && a.date > b.date)))
  ) {
    const swap = a; a = b; b = swap;
  }

  const monthA = MONTHS[a.month - 1];
  const monthB = MONTHS[b.month - 1];

  if (a.year !== b.year) {
    return `${monthA} ${a.date}, ${a.year} ${EN_DASH} ${monthB} ${b.date}, ${b.year}`;
  }
  if (a.month !== b.month) {
    return `${monthA} ${a.date} ${EN_DASH} ${monthB} ${b.date}, ${b.year}`;
  }
  if (a.date !== b.date) {
    return `${monthA} ${a.date}${EN_DASH}${b.date}, ${b.year}`;
  }
  return `${monthA} ${a.date}, ${a.year}`;
}

/** A project name that is safe to hand a filesystem, or the default. */
export function sanitizeName(projectName) {
  const raw = typeof projectName === 'string' ? projectName.trim() : '';
  if (!raw) return DEFAULT_VIDEO_NAME;
  const cleaned = raw
    .replace(ILLEGAL_RE, '-')
    .replace(/ {2,}/g, ' ')
    .trim();
  return cleaned || DEFAULT_VIDEO_NAME;
}

/**
 * The capture days the project's compiled video actually covers.
 *
 * Three sources, in order of authority:
 *   a) `photoCompile.compiledFrom.firstDay/lastDay` — written by the worker off
 *      the exact photos it encoded. Always right when present.
 *   b) the compile's day SELECTION resolved against the live coverage rollup,
 *      via the same resolveSelection() the compile CTA costs with. An estimate:
 *      it reads today's coverage, so a photo added on a new day inside an
 *      "all days" selection widens it. Good enough for a filename, and it is
 *      the only thing available for videos compiled before (a) existed.
 *   c) nothing.
 *
 * `unknown` — the bucket for photos with no readable capture time — is excluded
 * from (b): it has no place on a calendar, and it sorts after every real date
 * as a string, so including it would end every range at "unknown".
 */
function compiledCaptureDays(project, coverage) {
  const compileState = project?.photoCompile || null;
  const snapshot = compileState?.compiledFrom || null;

  const first = parseDay(snapshot?.firstDay);
  const last = parseDay(snapshot?.lastDay);
  if (first || last) {
    return { firstDay: snapshot?.firstDay, lastDay: snapshot?.lastDay };
  }

  if (!compileState || !coverage?.days) return null;
  // Same preference photosSinceCompile() uses: the selection the worker echoed
  // back describes what was encoded; the one on the queued marker may have been
  // replaced by a later edit.
  const selection = normalizeSelection(snapshot?.selection ?? compileState?.selection);
  const { days } = resolveSelection(selection, coverage.days);
  const dated = days.filter((d) => d !== UNKNOWN_DAY && DAY_RE.test(d)).sort();
  if (!dated.length) return null;
  return { firstDay: dated[0], lastDay: dated[dated.length - 1] };
}

/**
 * The capture days a COMBINED video covers, or null.
 *
 * The concat worker writes `videoConcat.combinedFrom.firstDay/lastDay` off the
 * clips it actually stream-copied, in the same UTC day keys the compile worker
 * uses — so, exactly like (a) above, this side never re-derives a day from a
 * timestamp and the two cannot drift.
 *
 * This is the ONLY source for a combine. There is deliberately no equivalent of
 * the (b) coverage branch: coverage is a rollup of PHOTO capture days, and a
 * combine consumes footage clips, so a combine made before this field existed
 * simply has no knowable range. That matches the header on those legacy objects
 * — they have none, so the card's blob fallback renames them `{project}.mp4`,
 * which is what a null return produces.
 */
function combinedCaptureDays(project) {
  const snapshot = project?.videoConcat?.combinedFrom || null;
  if (!parseDay(snapshot?.firstDay) && !parseDay(snapshot?.lastDay)) return null;
  return { firstDay: snapshot?.firstDay, lastDay: snapshot?.lastDay };
}

/**
 * The human capture range a project's source video covers, or '' when none
 * is knowable. Exported because the download card SHOWS this string as a stat
 * — reading it back out of the filename would break on a project whose own
 * name contains the separator.
 *
 * The live artifact's ORIGIN picks the branch outright — a combine answers from
 * its own worker snapshot, everything else from the compile's. Deliberately a
 * choice and not a `||` chain: falling through would let a combine with no
 * snapshot (a legacy one, or a run whose clips were all undated) be named off
 * `compiledCaptureDays`'s coverage branch, which resolves PHOTO days. A project
 * can hold both states at once — photos uploaded, a compile that FAILED leaving
 * `photoCompile` behind, then footage combined over the empty video slot — so
 * that range would be real, wrong, and stamped on a file whose own header says
 * `{project}.mp4`. The mismatch costs a multi-GB blob re-fetch on every
 * download, and the card shows the bogus dates as a stat.
 *
 * Origin is also what keeps a stale field out of a name in the other direction:
 * a project that was combined and then had a video UPLOADED over it still
 * carries the old `videoConcat` state. The storage trigger clears `combinedFrom`
 * on any non-concat origin too, but this gate is what covers docs written before
 * it started doing so.
 */
export function compiledCaptureRange(project, coverage = null) {
  const days = resolveVideoOrigin(project) === ORIGIN_VIDEO_CONCAT
    ? combinedCaptureDays(project)
    : compiledCaptureDays(project, coverage);
  return days ? formatCaptureRange(days.firstDay, days.lastDay) : '';
}

/**
 * What the source video should be called on disk — compiled from photos or
 * combined from footage clips, the grammar is the same.
 * `{project} – {range}.mp4`, or `{project}.mp4` when no range is knowable.
 */
export function compiledVideoFilename(project, coverage = null) {
  const name = sanitizeName(project?.projectName);
  const range = compiledCaptureRange(project, coverage);
  return range ? `${name} ${EN_DASH} ${range}.mp4` : `${name}.mp4`;
}

/**
 * The same name for a RENDERED edit, tagged so the two files are
 * distinguishable in a Downloads folder that holds both.
 */
export function editedVideoFilename(project, coverage = null) {
  const name = sanitizeName(project?.projectName);
  const range = compiledCaptureRange(project, coverage);
  return range
    ? `${name} ${EN_DASH} ${range} (edited).mp4`
    : `${name} (edited).mp4`;
}

/**
 * The filename a `Content-Disposition` header is serving, or null.
 *
 * RFC 6266: `filename*` (RFC 5987 percent-encoded, with a charset) wins over
 * the plain `filename`, which is only ever the ASCII fallback. Used to decide
 * whether the stored object already names itself correctly — if it does the
 * browser can stream it straight off the CDN, and if it doesn't we have to
 * re-name it locally through a blob.
 */
export function dispositionFilename(header) {
  if (typeof header !== 'string' || !header) return null;
  const extended = /filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[3].trim());
    } catch (err) {
      // A malformed percent-escape: fall through to the ASCII fallback rather
      // than throw inside a render.
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare) return bare[1].trim();
  return null;
}
